// Pure image-resize helpers for the editor's img node. Kept free of React/DOM so
// the drag math and clamping are unit-testable in the plain-Node vitest env.

// Smallest width (CSS px) an image can be shrunk to via the resize handles.
export const MIN_IMAGE_WIDTH = 48

export type ResizeSide = 'left' | 'right'

// Natural size of an image in CSS px: its pixel width divided by the device pixel
// ratio, so a 2x screenshot renders at half its raw pixel size (matches how images
// have always displayed here).
export function getNaturalDisplayWidth(naturalWidth: number, dpr: number): number {
    return Math.round(naturalWidth / (dpr || 1))
}

// The width (CSS px) an image should render at: the persisted `width` if set,
// otherwise the natural size derived from the stored `imgWidth`. Returns undefined
// when neither is known (the caller falls back to the DOM image's naturalWidth on load).
export function resolveImageDisplayWidth(
    element: { width?: number; imgWidth?: number },
    dpr: number
): number | undefined {
    if (typeof element.width === 'number' && element.width > 0) return element.width
    if (typeof element.imgWidth === 'number' && element.imgWidth > 0) {
        return getNaturalDisplayWidth(element.imgWidth, dpr)
    }
    return undefined
}

// New display width during a drag. `deltaX` is the pointer's X movement from where the
// drag started. The image is centered (SCD-949), so it resizes symmetrically around its
// center: dragging a side handle moves that edge by `deltaX` and mirrors the opposite edge,
// so the width changes by `2 * deltaX` and the grabbed handle tracks the pointer. The right
// handle widens on rightward movement, the left handle on leftward movement.
//
// Chosen small-image behavior (SCD-943): the width is capped at the natural display
// width, so an image is never upscaled past its own pixel size (never blurry). It can
// only shrink, down to MIN_IMAGE_WIDTH — or, for an image already narrower than that,
// it stays fixed at its natural width (the clamp range collapses).
export function computeResizeWidth(params: {
    startWidth: number
    deltaX: number
    side: ResizeSide
    naturalWidth: number
    dpr: number
    minWidth?: number
}): number {
    const { startWidth, deltaX, side, naturalWidth, dpr, minWidth = MIN_IMAGE_WIDTH } = params
    // Center-anchored: both edges move, so a `deltaX` pointer move changes width by `2 * deltaX`.
    const signedDelta = (side === 'right' ? deltaX : -deltaX) * 2
    const maxWidth = getNaturalDisplayWidth(naturalWidth, dpr)
    // Never let the lower bound exceed the cap (tiny images: max < minWidth).
    const lowerBound = Math.min(minWidth, maxWidth)
    const target = startWidth + signedDelta
    return Math.round(Math.min(Math.max(target, lowerBound), maxWidth))
}
